import React, { useMemo } from 'react';
import { ArrowRightIcon, CalendarDaysIcon, ChevronRightIcon, PlayIcon, TargetIcon } from 'lucide-react';
import { Employee, Lead, ProInvoice, ProJob, Quote, TimeEntry } from '../../types/pro';
import { JOB_STATUS, daysSince, formatTime, invoiceTotal, isSameDay, kr, quoteTotal } from '../../utils/pro';
import { PageHeader } from '../../components/pro/PageHeader';
import { StatusChip } from '../../components/pro/StatusChip';
import { EmptyState } from '../../components/pro/EmptyState';
import { BarChart } from '../../components/pro/BarChart';
interface ProDashboardProps {
  leads: Lead[];
  quotes: Quote[];
  jobs: ProJob[];
  invoices: ProInvoice[];
  employees: Employee[];
  timeEntries: TimeEntry[];
  onOpenJob: (jobId: string) => void;
  onOpenLead: (leadId: string) => void;
  onNavigate: (target: 'leads' | 'jobs' | 'invoices' | 'calendar') => void;
  onStartJob: (job: ProJob) => void;
}
const MONTHS = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
export function ProDashboard({
  leads,
  quotes,
  jobs,
  invoices,
  employees,
  onOpenJob,
  onOpenLead,
  onNavigate,
  onStartJob
}: ProDashboardProps) {
  const employeeById = useMemo(() => new Map(employees.map(e => [e.id ?? '', e])), [employees]);
  const kpis = useMemo(() => {
    const now = new Date();
    const monthRevenue = invoices.filter(invoice => {
      const date = new Date(invoice.invoiceDate);
      return invoice.status !== 'kladde' && date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    }).reduce((sum, invoice) => sum + invoiceTotal(invoice), 0);
    return {
      newLeads: leads.filter(lead => lead.stage === 'nyt').length,
      activeQuotes: quotes.filter(quote => ['sendt', 'set', 'kladde'].includes(quote.status)).length,
      activeJobs: jobs.filter(job => !['faerdig', 'faktureret'].includes(job.status)).length,
      overdue: invoices.filter(invoice => invoice.status === 'forfalden').length,
      monthRevenue
    };
  }, [leads, quotes, jobs, invoices]);
  const todaysJobs = useMemo(() => {
    const now = new Date();
    return jobs.filter(job => job.start && isSameDay(new Date(job.start), now)).sort((a, b) => (a.start ?? '').localeCompare(b.start ?? ''));
  }, [jobs]);
  const upcoming = useMemo(() => {
    const now = new Date();
    return jobs.filter(job => job.start && new Date(job.start) > now).sort((a, b) => (a.start ?? '').localeCompare(b.start ?? '')).slice(0, 4);
  }, [jobs]);
  const recentLeads = useMemo(() => [...leads].filter(lead => !['vundet', 'tabt'].includes(lead.stage)).sort((a, b) => b.createdDate.localeCompare(a.createdDate)).slice(0, 4), [leads]);
  const revenueByMonth = useMemo(() => {
    const now = new Date();
    return Array.from({
      length: 6
    }, (_, index) => {
      const date = new Date(now.getFullYear(), now.getMonth() - (5 - index), 1);
      const value = invoices.filter(invoice => {
        const invoiceDate = new Date(invoice.invoiceDate);
        return invoice.status !== 'kladde' && invoiceDate.getMonth() === date.getMonth() && invoiceDate.getFullYear() === date.getFullYear();
      }).reduce((sum, invoice) => sum + invoiceTotal(invoice), 0);
      return {
        label: MONTHS[date.getMonth()],
        value
      };
    });
  }, [invoices]);
  const activity = useMemo(() => {
    const entries: {
      id: string;
      text: string;
      date: string;
    }[] = [];
    quotes.slice(0, 3).forEach(quote => entries.push({
      id: `q-${quote.id}`,
      text: `Tilbud ${quote.quoteNo} til ${quote.customerName} — ${kr(quoteTotal(quote))}`,
      date: quote.createdDate
    }));
    invoices.slice(0, 3).forEach(invoice => entries.push({
      id: `i-${invoice.id}`,
      text: `Faktura ${invoice.invoiceNo} til ${invoice.customerName} — ${kr(invoiceTotal(invoice))}`,
      date: invoice.invoiceDate
    }));
    return entries.sort((a, b) => b.date.localeCompare(a.date)).slice(0, 5);
  }, [quotes, invoices]);
  return <div className="space-y-5" data-id="element-785" data-mp="ap5h6bxbzfco">
      <PageHeader title="Dashboard" subtitle={new Date().toLocaleDateString('da-DK', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    })} data-id="element-786" data-mp="aovo2fgzos2r" />

      {/* KPIs — revenue is the anchor, overdue turns orange when it matters */}
      <section aria-label="Nøgletal" className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-stone-200 bg-stone-200 lg:grid-cols-5" data-id="element-787" data-mp="5pj4q4pbu34x">
        {[{
        label: 'Nye leads',
        value: kpis.newLeads,
        target: 'leads' as const
      }, {
        label: 'Aktive tilbud',
        value: kpis.activeQuotes,
        target: 'jobs' as const
      }, {
        label: 'Aktive opgaver',
        value: kpis.activeJobs,
        target: 'jobs' as const
      }, {
        label: 'Forfaldne fakturaer',
        value: kpis.overdue,
        target: 'invoices' as const,
        alert: kpis.overdue > 0
      }].map(kpi => <button key={kpi.label} onClick={() => onNavigate(kpi.target)} className="bg-white p-4 text-left transition-colors hover:bg-stone-50 lg:p-5" data-id="element-788" data-mp="3s69qvuea8e4k">
            <p className={`text-[11px] font-semibold uppercase tracking-wider ${kpi.alert ? 'text-red-600' : 'text-stone-400'}`} data-id="element-789" data-mp="4u8ng7h5rn4j">
              {kpi.label}
            </p>
            <p className={`mt-1.5 text-2xl font-bold tabular-nums ${kpi.alert ? 'text-red-600' : 'text-stone-900'}`} data-id="element-790" data-mp="9tv3spsa9jhk">
              {kpi.value}
            </p>
          </button>)}
        <div className="col-span-2 bg-stone-900 p-4 lg:col-span-1 lg:p-5" data-id="element-791" data-mp="37og7pc61gh1b">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400" data-id="element-792" data-mp="3clzmqeak8xub">
            Omsætning denne måned
          </p>
          <p className="mt-1.5 text-2xl font-bold tabular-nums text-white" data-id="element-793" data-mp="3hesrv39xy86x">
            {kr(kpis.monthRevenue)}
          </p>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3" data-id="element-794" data-mp="3raw4x46bzr8n">
        {/* Today's jobs — the thing you actually act on */}
        <section className="overflow-hidden rounded-xl border border-stone-200 bg-white lg:col-span-2" data-id="element-795" data-mp="3ve2a08hpjcdj">
          <header className="flex items-center gap-2 border-b border-stone-100 px-5 py-4" data-id="element-796" data-mp="45envqbn7l4x">
            <h2 className="flex-1 text-base font-semibold text-stone-900" data-id="element-797" data-mp="2101adlec2lce">
              Dagens opgaver
            </h2>
            <button onClick={() => onNavigate('calendar')} className="inline-flex items-center gap-1 text-sm font-medium text-stone-500 transition-colors hover:text-brand-700" data-id="element-798" data-mp="25ykvjjl3rt3p">
              Kalender
              <ArrowRightIcon className="h-3.5 w-3.5" data-id="element-799" data-mp="1ooh28smtp6wd" />
            </button>
          </header>

          {todaysJobs.length === 0 ? <EmptyState icon={CalendarDaysIcon} title="Ingen opgaver i dag" description="Planlagte opgaver dukker op her på dagen." data-id="element-800" data-mp="1jqp1ra844cgk" /> : <ul className="divide-y divide-stone-100" data-id="element-801" data-mp="2cfmhb5kxvv5m">
              {todaysJobs.map(job => {
            const employee = employeeById.get(job.employeeIds[0] ?? '');
            return <li key={job.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3.5 transition-colors hover:bg-stone-50" data-id="element-802" data-mp="23cv2qwwroj9r">
                    <span className="w-12 flex-shrink-0 text-sm font-bold tabular-nums text-stone-900" data-id="element-803" data-mp="rxolrtvz9cu2">
                      {formatTime(job.start ?? '')}
                    </span>

                    <button onClick={() => job.id && onOpenJob(job.id)} className="min-w-0 flex-1 text-left" data-id="element-804" data-mp="2w3doi2rv2vkf">
                      <span className="block truncate text-sm font-medium text-stone-900" data-id="element-805" data-mp="2n1cwplfed1q5">
                        {job.title}
                      </span>
                      <span className="mt-0.5 block truncate text-xs text-stone-500" data-id="element-806" data-mp="3as0d6v3dozob">
                        {job.customerName} · {job.address}, {job.city}
                      </span>
                    </button>

                    {employee && <span className="hidden items-center gap-1.5 text-xs font-medium text-stone-600 sm:flex" data-id="element-807" data-mp="35zyxt0awysu2">
                        <span className="h-2 w-2 rounded-full" style={{
                  backgroundColor: employee.color
                }} data-id="element-808" data-mp="3tp1m2r01zjxj" />
                        {employee.name.split(' ')[0]}
                      </span>}

                    <StatusChip meta={JOB_STATUS[job.status]} data-id="element-809" data-mp="7pf3wft0tnyy" />

                    {job.status !== 'i-gang' && job.status !== 'faerdig' && job.status !== 'faktureret' && <button onClick={() => onStartJob(job)} className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg bg-stone-900 px-2.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-stone-800" data-id="element-810" data-mp="gi8kqho48vex">
                          <PlayIcon className="h-3 w-3" data-id="element-811" data-mp="2r3si0n2v5em" />
                          Start
                        </button>}
                  </li>;
          })}
            </ul>}
        </section>

        <div className="space-y-5" data-id="element-812" data-mp="195v0eoambqi5">
          <section className="rounded-xl border border-stone-200 bg-white p-5" data-id="element-813" data-mp="194jnnyz738ew">
            <h2 className="text-base font-semibold text-stone-900" data-id="element-814" data-mp="1ncjea6uyt0cm">
              Omsætning
            </h2>
            <p className="mb-4 text-xs text-stone-500" data-id="element-815" data-mp="1ie84fw4o6pue">Seneste 6 måneder</p>
            <BarChart data={revenueByMonth} format={kr} height={120} data-id="element-816" data-mp="1dm6xmpq9trbr" />
          </section>

          <section className="overflow-hidden rounded-xl border border-stone-200 bg-white" data-id="element-817" data-mp="18ndkwzit64cn">
            <header className="flex items-center gap-2 border-b border-stone-100 px-5 py-3.5" data-id="element-818" data-mp="14ls81e69xq2g">
              <h2 className="flex-1 text-base font-semibold text-stone-900" data-id="element-819" data-mp="zmp2lsmdthty">
                Seneste leads
              </h2>
              <button onClick={() => onNavigate('leads')} className="text-sm font-medium text-stone-500 transition-colors hover:text-brand-700" data-id="element-820" data-mp="un2prkagu3lo">
                Alle
              </button>
            </header>
            {recentLeads.length === 0 ? <EmptyState icon={TargetIcon} title="Ingen åbne leads" data-id="element-821" data-mp="2k20pt2gmpgm8" /> : <ul className="divide-y divide-stone-100" data-id="element-822" data-mp="2sjt3l2lc1zsd">
                {recentLeads.map(lead => <li key={lead.id} data-id="element-823" data-mp="2wkmmtintyqex">
                    <button onClick={() => lead.id && onOpenLead(lead.id)} className="flex w-full items-center gap-3 px-5 py-3 text-left transition-colors hover:bg-stone-50" data-id="element-824" data-mp="2wjlbdd2wk1wo">
                      <span className="min-w-0 flex-1" data-id="element-825" data-mp="29kovbw1m8a1r">
                        <span className="block truncate text-sm font-medium text-stone-900" data-id="element-826" data-mp="29m9nxg4ddiwz">
                          {lead.name}
                        </span>
                        <span className="block truncate text-xs text-stone-500" data-id="element-827" data-mp="2iku0s0n90yda">
                          {lead.jobType} · {lead.source}
                        </span>
                      </span>
                      <span className="flex-shrink-0 text-right" data-id="element-828" data-mp="3u11x3kc37dgi">
                        <span className="block text-sm font-semibold tabular-nums text-stone-900" data-id="element-829" data-mp="3u2wh8wfjb1jj">
                          {kr(lead.estimatedValue)}
                        </span>
                        <span className="block text-xs text-stone-400" data-id="element-830" data-mp="399mwv7xxion3">
                          {daysSince(lead.createdDate)} dage
                        </span>
                      </span>
                    </button>
                  </li>)}
              </ul>}
          </section>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2" data-id="element-831" data-mp="2qkqcpww8xcil">
        <section className="overflow-hidden rounded-xl border border-stone-200 bg-white" data-id="element-832" data-mp="2ql9hsd0ss6f7">
          <header className="border-b border-stone-100 px-5 py-3.5" data-id="element-833" data-mp="2lngfhv2m7wk7">
            <h2 className="text-base font-semibold text-stone-900" data-id="element-834" data-mp="30b1jdr7bdzt6">
              Kommende aftaler
            </h2>
          </header>
          {upcoming.length === 0 ? <EmptyState icon={CalendarDaysIcon} title="Ingen kommende aftaler" data-id="element-835" data-mp="27u8ljwr2aozc" /> : <ul className="divide-y divide-stone-100" data-id="element-836" data-mp="22vnkxw7we3ad">
              {upcoming.map(job => {
            const date = new Date(job.start ?? '');
            return <li key={job.id} data-id="element-837" data-mp="ibjtyl01zmz3">
                    <button onClick={() => job.id && onOpenJob(job.id)} className="group flex w-full items-center gap-3 px-5 py-3 text-left transition-colors hover:bg-stone-50" data-id="element-838" data-mp="n4l98u469oj7">
                      <span className="flex w-11 flex-shrink-0 flex-col items-center rounded-lg border border-stone-200 bg-stone-50 py-1.5" data-id="element-839" data-mp="dejoocb12o91">
                        <span className="text-[10px] font-semibold uppercase text-stone-500" data-id="element-840" data-mp="162p88vqci77a">
                          {date.toLocaleDateString('da-DK', {
                      weekday: 'short'
                    })}
                        </span>
                        <span className="text-base font-bold leading-none text-stone-800" data-id="element-841" data-mp="w6um3qmwxtzr">
                          {date.getDate()}
                        </span>
                      </span>
                      <span className="min-w-0 flex-1" data-id="element-842" data-mp="3gwbnoanl08gq">
                        <span className="block truncate text-sm font-medium text-stone-900" data-id="element-843" data-mp="x0qnz80o3do">
                          {job.title}
                        </span>
                        <span className="block truncate text-xs text-stone-500" data-id="element-844" data-mp="3s9llsa6y35sg">
                          {formatTime(job.start ?? '')} · {job.customerName}
                        </span>
                      </span>
                      <ChevronRightIcon className="h-4 w-4 flex-shrink-0 text-stone-300 transition-colors group-hover:text-brand-600" data-id="element-845" data-mp="jvd2xtu85tdh" />
                    </button>
                  </li>;
          })}
            </ul>}
        </section>

        <section className="overflow-hidden rounded-xl border border-stone-200 bg-white" data-id="element-846" data-mp="9vm8tt9ktgdx">
          <header className="border-b border-stone-100 px-5 py-3.5" data-id="element-847" data-mp="5vaqwo8llu65">
            <h2 className="text-base font-semibold text-stone-900" data-id="element-848" data-mp="2uopnb6rk6xad">
              Seneste aktivitet
            </h2>
          </header>
          {activity.length === 0 ? <EmptyState title="Ingen aktivitet endnu" data-id="element-849" data-mp="164aesrmnewru" /> : <ul className="divide-y divide-stone-100" data-id="element-850" data-mp="161xp1sanfi48">
              {activity.map(entry => <li key={entry.id} className="flex items-start gap-3 px-5 py-3 text-sm" data-id="element-851" data-mp="1a3j3hi2u04gj">
                  <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-stone-300" data-id="element-852" data-mp="1jvow8u0wqpws" />
                  <span className="min-w-0 flex-1 truncate text-stone-700" data-id="element-853" data-mp="1ovba95ok8a6n">
                    {entry.text}
                  </span>
                  <span className="flex-shrink-0 text-xs text-stone-400" data-id="element-854" data-mp="1ts1qbt0d75s2">
                    {daysSince(entry.date)} d
                  </span>
                </li>)}
            </ul>}
        </section>
      </div>
    </div>;
}